import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRollbar } from '@rollbar/react';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
  type BatteryLeadFormData,
  submitBatteryLeadToGoogle,
} from '@/clients/googleSheetClient';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import { RadioGroup } from '@/components/BaseLibrary/RadioGroup/RadioGroup';
import Checkbox from '@/components/Checbox/Checkbox';
import LeadFormContainer from '@/components/Form/LeadFormContainer/LeadFormContainer';
import { LeadFormTCPA } from '@/components/Form/LeadFormTCPA/LeadFormTCPA';
import type { DialogContent } from '@/components/FormResultDialog/FormResultDialog';
import type { GoogleUrls } from '@/constants/baseUrls';
import { getFormType, LeadFormType } from '@/enums/FormType';
import useEligibility, { EligibilityType } from '@/hooks/useEligibility';
import { useSubmitLeadToSolarEdge } from '@/hooks/useSubmitLeadToSolarEdge';
import {
  type LeadFormInputData,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import useGlobalStyles from '@/styles/global.styles';
import type { ActionModalCF } from '@/types';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import { InputName, OwnHome } from '@/utils/validation/inputs';
import useStyles from './LeadForm.styles';
import LeadFormContent from './LeadFormContent';
import {
  LeadFormResult,
  type LeadFormResultType,
} from './LeadFormResult/LeadFormResult';
import {
  fullNameWithEmailSchema,
  fullNameWithoutEmailSchema,
  LeadFormSchemaMode,
  type LeadFormSchemaType,
  separateNamesWithEmailSchema,
  separateNamesWithoutEmailSchema,
} from './schema';

export enum LeadFormStyle {
  ACTION_USER_INPUT = 'actionUserInput',
  HERO = 'hero',
}

export enum FormOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum DialogType {
  SUCCESS = 'success',
  FAILURE = 'failure',
}

const FORM_SUBMIT_ERROR_MESSAGE =
  'There was an error submitting the form, please try again';

interface LeadFormProps {
  campaignId?: string;
  heading?: string;
  ownerQuestion?: string;
  hideHomeOwnershipQuestion?: boolean;
  submitButtonText?: string;
  tcpa: RichTextDocument;
  successDialogContent?: DialogContent | ActionModalCF;
  failDialogContent?: DialogContent | ActionModalCF;
  orientation?: FormOrientation;
  isOnDark?: boolean;
  googleSheetUrls?: GoogleUrls;
  solarEdgeSubmissionUrl?: string;
  formType?: LeadFormType;
  overrideCampaignId?: boolean;
  leadFormstyle?: LeadFormStyle;
  hideEmailInput?: boolean;
  redirectUrl?: string;
  outOfMarketRedirectUrl?: string;
  shouldRedirectWithParams?: boolean;
  useFullName?: boolean;
  requiresTcpaAcknowledgment?: boolean;
}

export function LeadForm({
  campaignId: contentfulCampaignId,
  heading,
  ownerQuestion,
  hideHomeOwnershipQuestion = false,
  submitButtonText,
  tcpa,
  successDialogContent,
  failDialogContent,
  orientation = FormOrientation.VERTICAL,
  isOnDark = false,
  googleSheetUrls,
  solarEdgeSubmissionUrl,
  formType,
  overrideCampaignId,
  leadFormstyle = LeadFormStyle.ACTION_USER_INPUT,
  hideEmailInput = false,
  redirectUrl,
  outOfMarketRedirectUrl,
  shouldRedirectWithParams,
  useFullName = false,
  requiresTcpaAcknowledgment = false,
}: LeadFormProps) {
  const { schema: formSchema, mode: schemaMode } = (() => {
    if (useFullName) {
      return hideEmailInput
        ? {
            schema: fullNameWithoutEmailSchema,
            mode: LeadFormSchemaMode.FULL_NAME_WITHOUT_EMAIL,
          }
        : {
            schema: fullNameWithEmailSchema,
            mode: LeadFormSchemaMode.FULL_NAME_WITH_EMAIL,
          };
    }
    return hideEmailInput
      ? {
          schema: separateNamesWithoutEmailSchema,
          mode: LeadFormSchemaMode.SEPARATE_NAMES_WITHOUT_EMAIL,
        }
      : {
          schema: separateNamesWithEmailSchema,
          mode: LeadFormSchemaMode.SEPARATE_NAMES_WITH_EMAIL,
        };
  })();

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitSuccessful, isValid },
    trigger,
  } = useForm<LeadFormSchemaType>({
    resolver: zodResolver(formSchema),
    mode: 'onTouched',
    defaultValues: {
      mode: schemaMode,
      [InputName.TCPA_ACKNOWLEDGMENT]: false,
      [InputName.OWN_HOME]: OwnHome.OWN,
    },
  });
  const { classes, cx } = useStyles({ isOnDark });
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { submitLeadToZeus, trackFormStarted } = useSubmitLeadToZeus();
  const { submitLeadToSolarEdge } = useSubmitLeadToSolarEdge();
  const { fetchEligibility } = useEligibility();
  const [result, setResult] = useState<LeadFormResultType>(null);
  const rollbar = useRollbar();

  const isVertical = orientation === FormOrientation.VERTICAL;

  const type = getFormType(formType);
  const isTcpaAcknowledged = watch(InputName.TCPA_ACKNOWLEDGMENT);
  const tcpaCheckboxSlot = requiresTcpaAcknowledgment ? (
    <span className={classes.checkbox}>
      <Checkbox
        className='tcpa-checkbox'
        aria-label='TCPA acknowledgment'
        disabled={isSubmitSuccessful}
        {...register(InputName.TCPA_ACKNOWLEDGMENT)}
      />
    </span>
  ) : null;

  const OwnHomeRadios = (
    <RadioGroup
      legend={isVertical ? ownerQuestion : undefined}
      options={[
        { label: 'Yes', value: OwnHome.OWN },
        { label: 'No', value: OwnHome.RENT },
      ]}
      trigger={trigger}
      register={register}
      name={InputName.OWN_HOME}
      errors={errors}
      isOnDark={!isVertical && isOnDark}
      disabled={isSubmitSuccessful}
      className={cx(
        isVertical &&
          (leadFormstyle === LeadFormStyle.HERO
            ? classes.radioGroupHero
            : classes.radioGroup)
      )}
      isSmall={leadFormstyle === LeadFormStyle.HERO}
    />
  );

  const onSubmit: SubmitHandler<LeadFormSchemaType> = async (
    data: LeadFormSchemaType
  ) => {
    let firstName: string;
    let lastName: string;
    let email = '';

    if ('fullName' in data) {
      const nameParts = data.fullName.trim().split(' ');
      firstName = nameParts[0];
      lastName = nameParts.slice(1).join(' ');
    } else {
      firstName = data.firstName;
      lastName = data.lastName;
    }

    if ('email' in data) {
      email = data.email;
    }

    const { zipCode, phone, ownHome, address } = data;
    let isEligible: boolean;

    try {
      if (type === LeadFormType.ZEUS) {
        isEligible = !!fetchEligibility(EligibilityType.WEBSITE, zipCode);
        const zeusLeadFormData: LeadFormInputData = {
          first_name: firstName,
          last_name: lastName,
          email,
          zip: zipCode,
          phone_home: phone,
          property_ownership: ownHome as OwnHome,
        };

        await submitLeadToZeus({
          zeusData: zeusLeadFormData,
          contentfulCampaignId,
          overrideCampaignId,
          honeypot: Boolean(address),
        });
      } else if (type === LeadFormType.GOOGLE_SHEETS) {
        isEligible = !!fetchEligibility(EligibilityType.WEBSITE, zipCode);
        if (googleSheetUrls) {
          const batteryLeadFormData: BatteryLeadFormData = {
            firstName,
            lastName,
            email,
            phone,
            zip: zipCode,
          };
          await submitBatteryLeadToGoogle({
            batteryData: batteryLeadFormData,
            googleSheetUrls,
            propertyOwnership: ownHome,
            honeypot: Boolean(address),
          });
        }
      } else if (type === LeadFormType.SOLAR_EDGE) {
        const solarEdgeResponse = await submitLeadToSolarEdge({
          solarEdgeData: {
            first_name: firstName,
            last_name: lastName,
            phone_1: phone,
            postal_code: zipCode,
            ...(email && { email }),
          },
          submissionUrl: solarEdgeSubmissionUrl,
        });

        isEligible = solarEdgeResponse.outcome === 'success';
      } else {
        throw new Error('Invalid type provided.');
      }
    } catch (err: unknown) {
      rollbar.error(getErrorMessage(err), getErrorObject(err));
      setError('root.formSubmitError', {
        type: 'formSubmit',
        message: FORM_SUBMIT_ERROR_MESSAGE,
      });
      return;
    }

    let params = null;

    if (shouldRedirectWithParams) {
      params = new URLSearchParams({ firstName, lastName, zipCode, phone });
      if (email) params.append('email', email);
    }

    setResult({
      isEligible,
      params,
      redirectData: {
        success: redirectUrl,
        failure: outOfMarketRedirectUrl,
      },
      modalData: {
        success: successDialogContent,
        failure: failDialogContent,
      },
    });
  };

  return (
    <LeadFormContainer
      className={cx(
        isVertical &&
          (leadFormstyle === LeadFormStyle.HERO
            ? classes.formHero
            : classes.form)
      )}
      onFocusCapture={trackFormStarted}
      onSubmit={handleSubmit(onSubmit)}
    >
      <h2 className={cx(globalClasses.h3, classes.formHeading)}>{heading}</h2>
      {leadFormstyle === LeadFormStyle.ACTION_USER_INPUT && (
        <span className={cx(globalClasses.bodySm, classes.formSubHeading)}>
          All fields are required.
        </span>
      )}
      <fieldset
        disabled={isSubmitSuccessful}
        aria-label='Lead form fields'
        style={{ border: 0, margin: 0, minInlineSize: 0, padding: 0 }}
      >
        <LeadFormContent
          isOnDark={isOnDark}
          isVertical={isVertical}
          hideHomeOwnershipQuestion={hideHomeOwnershipQuestion}
          classes={classes}
          globalClasses={globalClasses}
          ownHomeRadios={OwnHomeRadios}
          contentStyle={
            leadFormstyle === LeadFormStyle.HERO
              ? classes.formContentHero
              : classes.formContent
          }
          evenSpacing={leadFormstyle === LeadFormStyle.HERO}
          register={register}
          errors={errors}
          schemaMode={schemaMode}
        />
        <LeadFormTCPA
          text={tcpa}
          checkboxSlot={tcpaCheckboxSlot}
          className={
            leadFormstyle === LeadFormStyle.HERO
              ? classes.tcpaHero
              : classes.tcpa
          }
        />
        {errors?.root?.formSubmitError && (
          <p className={classes.submitError}>
            {errors.root.formSubmitError.message}
          </p>
        )}
      </fieldset>
      <div
        className={cx(
          isVertical
            ? classes.buttonContainer
            : classes.horizontalButtonContainer
        )}
      >
        <Button
          type='submit'
          variant={ButtonVariant.PRIMARY}
          label={submitButtonText}
          size={ButtonSize.MD}
          className={
            isVertical
              ? leadFormstyle === LeadFormStyle.HERO
                ? classes.buttonHero
                : classes.button
              : classes.horizontalButton
          }
          disabled={
            Object.keys(errors).length > 0 ||
            isSubmitSuccessful ||
            !isValid ||
            (requiresTcpaAcknowledgment && !isTcpaAcknowledged)
          }
          isOnDark={isOnDark}
        />
      </div>
      <LeadFormResult result={result} />
    </LeadFormContainer>
  );
}
